import { ImageOff, Volume2, VolumeX } from "lucide-react"
import type { Media } from "@/shared/lib/types"
import { Button } from "@/shared/ui/button"
import { Skeleton } from "@/shared/ui/skeleton"
import { usePostCardMedia } from "../model/use-post-card-media"

interface PostCardMediaProps {
	media?: Media[]
	title?: string | null
}

export function PostCardMedia({ media, title }: PostCardMediaProps) {
	const {
		hasMediaAttempt,
		hasMediaError,
		previewSrc,
		videoSrc,
		videoPosterSrc,
		isMediaLoaded,
		isVideoPosterLoaded,
		isVideoReady,
		shouldLoadVideo,
		isMuted,
		videoIntersectionRatio,
		mediaContainerRef,
		videoRef,
		mediaLoadedRef,
		setIsMediaLoaded,
		setIsVideoPosterLoaded,
		setIsVideoReady,
		setHasMediaError,
		toggleMute,
	} = usePostCardMedia({ media, title })

	if (!hasMediaAttempt) return null

	if (hasMediaError) {
		return (
			<div className="relative flex aspect-video w-full items-center justify-center gap-2 bg-muted px-4 text-center">
				<ImageOff className="h-5 w-5 shrink-0 text-muted-foreground" />
				<p className="text-sm text-muted-foreground">Не удалось загрузить превью</p>
			</div>
		)
	}

	return (
		<div ref={mediaContainerRef} className="relative aspect-video w-full overflow-hidden bg-muted">
			{videoSrc ? (
				<>
					{videoPosterSrc ? (
						<img
							src={videoPosterSrc}
							alt={title || "Preview"}
							loading="eager"
							decoding="async"
							onLoad={() => {
								mediaLoadedRef.current = true
								setIsVideoPosterLoaded(true)
							}}
							onError={() => setHasMediaError(true)}
							onContextMenu={(e) => e.preventDefault()}
							onDragStart={(e) => e.preventDefault()}
							className="h-full w-full object-cover"
							style={{ userSelect: "none", pointerEvents: "none" }}
						/>
					) : null}

					{(!videoPosterSrc || !isVideoPosterLoaded) && (
						<Skeleton className="pointer-events-none absolute inset-0" />
					)}

					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="absolute right-1 bottom-1 z-10 size-11 rounded-full p-0 text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)] hover:bg-transparent hover:text-white"
						aria-label={isMuted ? "Включить звук" : "Выключить звук"}
						onClick={(e) => {
							e.preventDefault()
							e.stopPropagation()
							toggleMute()
						}}
					>
						{isMuted ? <VolumeX className="size-5" /> : <Volume2 className="size-5" />}
					</Button>

					{shouldLoadVideo ? (
						<>
							{!isVideoReady && <Skeleton className="pointer-events-none absolute inset-0" />}
							<video
								ref={videoRef}
								src={videoSrc}
								muted={isMuted}
								loop
								disablePictureInPicture
								playsInline
								preload="metadata"
								onCanPlay={() => {
									mediaLoadedRef.current = true
									setIsVideoReady(true)
									if (videoIntersectionRatio >= 0.85) {
										const el = videoRef.current
										if (!el) return
										el.muted = isMuted
										const p = el.play()
										if (p) p.catch(() => undefined)
									}
								}}
								onError={() => setHasMediaError(true)}
								onContextMenu={(e) => e.preventDefault()}
								className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-200 ${
									isVideoReady ? "opacity-100" : "opacity-0"
								}`}
								style={{ userSelect: "none", pointerEvents: "none" }}
							/>
						</>
					) : null}
				</>
			) : previewSrc ? (
				<>
					{!isMediaLoaded && <Skeleton className="pointer-events-none absolute inset-0" />}
					<img
						src={previewSrc}
						alt={title || "Preview"}
						loading="eager"
						decoding="async"
						onLoad={() => {
							mediaLoadedRef.current = true
							setIsMediaLoaded(true)
						}}
						onError={() => setHasMediaError(true)}
						onContextMenu={(e) => e.preventDefault()}
						onDragStart={(e) => e.preventDefault()}
						className={`h-full w-full object-cover transition-opacity duration-200 ${
							isMediaLoaded ? "opacity-100" : "opacity-0"
						}`}
						style={{ userSelect: "none" }}
					/>
				</>
			) : null}
		</div>
	)
}
